// frontend/packages/business-console/src/pages/Scheduling.jsx
//
// Scheduling Phase A (D35) Console home. One nav entry ("Scheduling", in the
// Customers group) that lands on a tabbed page so every surface is <=2 clicks:
//   - Calendar:     bookings list + lifecycle actions (cancel/reschedule/
//                   no-show/complete). No drag calendar in Phase A.
//   - Approvals:    request-to-book queue (approve/decline).
//   - Availability: staff, weekly hours + overrides, time off, appointment
//                   types.
//   - Settings:     the two business-level toggles (crmSync, defaultBookingMode).
//
// Self-gates on the `scheduling` tenant feature (GET /api/v2/features), the
// same "routed + listed for all, friendly not-available state when off"
// pattern as RyzzeCast / UpRyzze AI. The authed API also 404s when off, so the
// tabs degrade to empty even if the gate check were skipped.
//
// D20: advances P1 (tenants self-serve their availability, no founder touch)
// and P3 (every write already emits a D6 audit artifact server-side).

import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';

import {
  getSchedulingEnabled,
  listPendingApprovals,
} from '../services/schedulingService';
import { PageHeader, Surface, useTokens } from '../components/primitives';
import CalendarTab from './Scheduling/CalendarTab';
import ApprovalsTab from './Scheduling/ApprovalsTab';
import AvailabilityTab from './Scheduling/AvailabilityTab';
import SettingsTab from './Scheduling/SettingsTab';

const TABS = [
  { key: 'calendar', label: 'Calendar' },
  { key: 'approvals', label: 'Approvals' },
  { key: 'availability', label: 'Availability' },
  { key: 'settings', label: 'Settings' },
];

export default function Scheduling() {
  const t = useTokens();
  const [gate, setGate] = useState({ loading: true, enabled: false });
  const [tab, setTab] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  // Pending-approvals count drives the badge on the Approvals tab. Refreshed
  // when the gate opens and whenever an approval action reports it changed.
  const refreshPendingCount = useCallback(async () => {
    try {
      const page = await listPendingApprovals({ limit: 1 });
      setPendingCount(Number(page?.total || 0));
    } catch {
      // Non-fatal: the badge just stays as-is.
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const status = await getSchedulingEnabled();
      if (!alive) return;
      setGate({ loading: false, enabled: Boolean(status?.enabled) });
      if (status?.enabled) refreshPendingCount();
    })();
    return () => {
      alive = false;
    };
  }, [refreshPendingCount]);

  if (gate.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        maxWidth: 1280,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <PageHeader
        title="Scheduling"
        meta={
          <Typography
            sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600 }}
          >
            Bookings, approvals, staff availability, and services.
          </Typography>
        }
      />

      {!gate.enabled ? (
        <Surface
          sx={{
            borderStyle: 'dashed',
            py: 6,
            px: 3,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <EventAvailableOutlinedIcon
            sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.6 }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Online booking isn&apos;t turned on for your account yet
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', maxWidth: 420 }}
          >
            Contact UpRyzze to enable it. Once it is on, your customers can book
            from your website and you can manage staff, availability, and
            approvals right here.
          </Typography>
        </Surface>
      ) : (
        <>
          <Box sx={{ borderBottom: `1px solid ${t.hairline}` }}>
            <Tabs
              value={tab}
              onChange={(_e, next) => setTab(next)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {TABS.map((tabDef, idx) => (
                <Tab
                  key={tabDef.key}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                  label={
                    tabDef.key === 'approvals' && pendingCount > 0 ? (
                      <Badge
                        color="error"
                        badgeContent={pendingCount}
                        max={99}
                        sx={{ '& .MuiBadge-badge': { right: -12, top: 2 } }}
                      >
                        {tabDef.label}
                      </Badge>
                    ) : (
                      tabDef.label
                    )
                  }
                  id={`scheduling-tab-${idx}`}
                />
              ))}
            </Tabs>
          </Box>

          <Box role="tabpanel" hidden={tab !== 0}>
            {tab === 0 ? <CalendarTab /> : null}
          </Box>
          <Box role="tabpanel" hidden={tab !== 1}>
            {tab === 1 ? (
              <ApprovalsTab onChanged={refreshPendingCount} />
            ) : null}
          </Box>
          <Box role="tabpanel" hidden={tab !== 2}>
            {tab === 2 ? <AvailabilityTab /> : null}
          </Box>
          <Box role="tabpanel" hidden={tab !== 3}>
            {tab === 3 ? <SettingsTab /> : null}
          </Box>
        </>
      )}
    </Box>
  );
}
